import clsx from 'clsx'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon.js'

/**
 * A toolbar button and the panel it opens.
 *
 * The panel is rendered into `document.body` at fixed coordinates rather than
 * absolutely inside the button. `Card` clips its overflow so a list can round
 * its own corners, which means an in-flow panel would be cut off at the first
 * row of the list underneath it.
 *
 * It flips above the trigger when there is more room up than down, so the
 * bottom of a long page still opens a usable menu, and it repositions on scroll
 * rather than closing: a menu that vanishes when the page moves under a
 * trackpad's inertia feels broken.
 */

interface Placement {
  left: number
  top?: number
  bottom?: number
}

const GAP = 6
const EDGE = 8

export function Popover({
  label,
  icon,
  count,
  title,
  width = 260,
  grouped = false,
  children,
}: {
  label: ReactNode
  icon?: ReactNode
  /** Shown as a pill on the button. Omitted when zero. */
  count?: number
  title?: string
  width?: number
  /** Drop the button's own ring and radius, for use inside a `ButtonGroup`. */
  grouped?: boolean
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [placement, setPlacement] = useState<Placement | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()

  const close = useCallback(() => setOpen(false), [])

  const position = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return

    const rect = trigger.getBoundingClientRect()
    const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - width - EDGE))
    const below = window.innerHeight - rect.bottom

    setPlacement(
      below < 260 && rect.top > below
        ? { left, bottom: window.innerHeight - rect.top + GAP }
        : { left, top: rect.bottom + GAP },
    )
  }, [width])

  useLayoutEffect(() => {
    if (open) position()
  }, [open, position])

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      // Otherwise Escape also closes whatever dialog the list is sitting in.
      event.stopPropagation()
      setOpen(false)
      triggerRef.current?.focus()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', position)
    // Capturing, so a scroll in any ancestor moves the panel with the button.
    window.addEventListener('scroll', position, true)

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', position)
      window.removeEventListener('scroll', position, true)
    }
  }, [open, position])

  const active = open || (count ?? 0) > 0

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        title={title}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((was) => !was)}
        className={clsx(
          'inline-flex min-h-[var(--control-md)] items-center gap-1.5 whitespace-nowrap',
          'px-[var(--control-pad-md)] text-xs font-medium transition-colors',
          grouped ? 'bg-white' : 'rounded-md bg-white ring-1 ring-inset ring-ink-300',
          active ? 'text-ink-900' : 'text-ink-600',
          'hover:bg-ink-50 hover:text-ink-900',
        )}
      >
        {icon}
        {label}
        {count !== undefined && count > 0 ? (
          <span className="rounded-full bg-brand-800 px-1.5 text-[10px] font-semibold text-white tabular-nums">
            {count}
          </span>
        ) : null}
        <Icon
          name="chevronDown"
          className={clsx('size-3 transition-transform', open && 'rotate-180')}
          strokeWidth={2.25}
        />
      </button>

      {open && placement
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              style={{ left: placement.left, top: placement.top, bottom: placement.bottom, width }}
              className="fixed z-50 max-w-[calc(100vw-16px)] rounded-lg bg-white p-1 ring-1 ring-ink-200 shadow-lg"
            >
              {children(close)}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

/** The label above a group of menu rows, with an optional Clear on the right. */
export function PopoverHeading({
  children,
  action,
}: {
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-2 px-2 pt-1 pb-1.5">
      <span className="text-[10px] font-semibold tracking-wide text-ink-500 uppercase">
        {children}
      </span>
      {action}
    </div>
  )
}

export function PopoverDivider() {
  return <div className="my-1 h-px bg-ink-100" />
}

/**
 * One selectable row. `mark` is a box for multi-select and a disc for
 * single-select, which is the only signal that says whether picking a second
 * option adds to the first or replaces it.
 */
export function PopoverOption({
  label,
  detail,
  state,
  mark = 'box',
  onClick,
}: {
  label: ReactNode
  detail?: ReactNode
  state: 'none' | 'some' | 'all'
  mark?: 'box' | 'disc'
  onClick: () => void
}) {
  const on = state !== 'none'

  return (
    <button
      type="button"
      // `mixed` is exactly the half-selected case, and the only value that
      // reads correctly for a stage with some of its statuses chosen.
      aria-pressed={state === 'some' ? 'mixed' : on}
      onClick={onClick}
      className="flex w-full min-h-[var(--control-sm)] items-center gap-2 rounded-md px-2 py-1 text-left text-xs text-ink-800 transition-colors hover:bg-ink-50"
    >
      <span
        className={clsx(
          'flex size-4 shrink-0 items-center justify-center ring-1 ring-inset',
          mark === 'disc' ? 'rounded-full' : 'rounded-sm',
          on ? 'bg-brand-800 text-white ring-brand-800' : 'bg-white ring-ink-300',
        )}
      >
        {state === 'all' ? (
          <Icon name="check" className="size-3" strokeWidth={2.5} />
        ) : state === 'some' ? (
          <span className="h-0.5 w-2 rounded-full bg-white" />
        ) : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {detail ? <span className="shrink-0 text-[11px] text-ink-500">{detail}</span> : null}
    </button>
  )
}
